import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, copyToClipboard } from "@/lib/api";
import AppShell from "@/components/AppShell";
import SitesPanel from "@/components/SitesPanel";
import MappingTable from "@/components/MappingTable";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FlashReportActions } from "@/components/FlashReportActions";
import { EmailDraftsPanel } from "@/components/EmailDraftsPanel";
import { ArrowLeft, Copy } from "@phosphor-icons/react";

export default function ClientManage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [client, setClient] = useState(null);
  const [activeSiteId, setActiveSiteId] = useState(null);
  const [siteName, setSiteName] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/clients");
    setClient(data.find((c) => c.id === id) || null);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // When the active site changes, look up its display name from the sites endpoint
  useEffect(() => {
    if (!activeSiteId || !id) { setSiteName(""); return; }
    api.get(`/clients/${id}/sites`).then(({ data }) => {
      const all = [data.parent, ...data.sites];
      const s = all.find((x) => x.id === activeSiteId);
      setSiteName(s?.name || "");
    });
  }, [activeSiteId, id]);

  useEffect(() => {
    if (sp.get("xero") === "connected") toast.success("Xero connected successfully");
  }, [sp]);

  if (!client) return <AppShell title="Loading..." />;

  const copyLink = async () => {
    const url = `${window.location.origin}/c/${client.magic_token}`;
    const ok = await copyToClipboard(url);
    if (ok) toast.success("Magic link copied");
    else toast.message("Copy not allowed here", { description: url });
  };

  const scopeId = activeSiteId || client.id;
  const scopeLabel = activeSiteId && siteName ? siteName : `${client.name} (primary)`;

  return (
    <AppShell
      subtitle={client.location || "Client management"}
      title={client.name}
      actions={
        <div className="flex items-center gap-3">
          <Link to={`/c/${client.magic_token}`} target="_blank" className="text-sm text-[#0F172A] hover:underline flex items-center gap-1.5" data-testid="open-flash-report">
            Flash Report →
          </Link>
          <Link to="/admin" className="text-sm text-[#475569] hover:text-[#0F172A] flex items-center gap-2">
            <ArrowLeft size={16} /> All clients
          </Link>
        </div>
      }
    >
      {/* Magic link strip */}
      <div className="bg-white border border-[#E2E8F0] rounded-md p-6 card-fade mb-6">
        <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-3">Magic Link · share with client</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-[#e2e2e2] border border-[#E2E8F0] rounded-md px-3 py-2.5 font-mono text-xs text-[#0F172A] truncate" data-testid="magic-link-display">
            {window.location.origin}/c/{client.magic_token}
          </div>
          <Button onClick={copyLink} data-testid="copy-magic-link" className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md">
            <Copy size={14} className="mr-1.5" /> Copy
          </Button>
          <FlashReportActions clientId={client.id} clientName={client.name} label="Flash Report" testid="flash-report-actions" className="h-10 px-3" />
        </div>
        <p className="text-xs text-[#94A3B8] font-mono mt-2">A single link unlocks the Flash Report for all sites under this client.</p>
      </div>

      {/* Sites manager */}
      <EmailDraftsPanel clientId={client.id} />
      <div className="mb-6">
        <SitesPanel
          clientId={id}
          activeSiteId={activeSiteId || client.id}
          onSelectSite={(sid) => setActiveSiteId(sid === client.id ? null : sid)}
        />
      </div>

      {/* Mapping table for the active site */}
      <div>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-1">Account Code Mapping</p>
            <h2 className="font-display text-2xl tracking-tight">
              {scopeLabel}
            </h2>
            <p className="text-xs text-[#475569] mt-1">Classify each Xero account as Wet (drinks), Dry (food), Other or Inter-company. Click a chip to set; bulk-save when ready.</p>
          </div>
        </div>
        <MappingTable
          clientId={id}
          siteId={activeSiteId}
          canImportFromXero={scopeId === client.id ? client.xero_connected : true}
        />
      </div>
    </AppShell>
  );
}
